import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import { computed, ref } from 'vue'
import type { Config } from '@/ts/types/config.types'
import type { FormField } from '@/ts/types/schema.types'

interface CalculationStoreState {
  config: Config | undefined
  calculationResult: CalculationResult
  errors: string[]
}

export interface CalculationResult {
  private_buy: {
    grossSalary: number | null
    employerSubsidy: null
    leasingRate: null
    monetaryAdvantage: null
    calculationBasis: number | null
    tax: number | null
    socialInsurance: number | null
    netPayout: number | null
    netBurdenDuration: number | null
    purchasePrice: number | null
    insuranceCost: number | null
    expectedUsedPurchasePrice: number | null
    totalCost: number | null
  }
  leasing: {
    grossSalary: number | null
    employerSubsidy: number | null
    leasingRate: number | null
    monetaryAdvantage: number | null
    calculationBasis: number | null
    tax: number | null
    socialInsurance: number | null
    netPayout: number | null
    netBurdenDuration: number | null
    purchasePrice: string | null
    insuranceCost: string | null
    expectedUsedPurchasePrice: number | null
    totalCost: number | null
    insuranceBonus: number | null
  }
  monthlyNetBurden: number | null
  absoluteSavings: number | null
  relativeSavings: number | null
}

export interface SocialInsuranceRequiredFields {
  [key: string]: FormField | undefined
}

export interface CalculationStoreStatePatch {}

export const useCalculationStore = defineStore('calculation', () => {
  const _state: Ref<CalculationStoreState> = ref({
    config: undefined,
    errors: [],
    calculationResult: {
      private_buy: {
        grossSalary: 0,
        employerSubsidy: null,
        leasingRate: null,
        monetaryAdvantage: null,
        calculationBasis: 3000,
        tax: 0,
        socialInsurance: 0,
        netPayout: 0,
        netBurdenDuration: 0,
        purchasePrice: 0,
        insuranceCost: 0,
        expectedUsedPurchasePrice: 0,
        totalCost: 0
      },
      leasing: {
        grossSalary: 0,
        employerSubsidy: 0,
        leasingRate: 0,
        monetaryAdvantage: 0,
        calculationBasis: 0,
        tax: 0,
        socialInsurance: 0,
        netPayout: 0,
        netBurdenDuration: 0,
        purchasePrice: 'inkl.',
        insuranceCost: 'inkl.',
        expectedUsedPurchasePrice: 0,
        totalCost: 0,
        insuranceBonus: 0
      },
      monthlyNetBurden: 0,
      absoluteSavings: 0,
      relativeSavings: 0
    }
  })
  const config = computed(() => _state.value.config)
  const calculatedResults = computed(() => _state.value.calculationResult)
  const errors = computed(() => _state.value.errors)

  function initConfig(config: Config) {
    _state.value.config = config
  }

  function patchCalculations(patch: CalculationStoreStatePatch) {
    Object.entries(patch).forEach(([calculationKey, calculationValue]) => {
      if (calculationKey === 'leasing' || calculationKey === 'private_buy') {
        // @ts-ignore
        Object.entries(patch[calculationKey]).forEach(
          // @ts-ignore
          ([innerKey, innerValue]) => (_state.value.calculationResult[calculationKey][innerKey] = innerValue)
        )
        return
      }
      // @ts-ignore
      _state.value.calculationResult[calculationKey] = calculationValue
    })
  }

  function updateCalculationErrors(errors?: string[]) {
    _state.value.errors = errors ?? []
  }

  return {
    calculatedResults,
    config,
    errors,
    initConfig,
    patchCalculations,
    updateCalculationErrors
  }
})
